/**
 * Settings Page - Business Configuration
 * 
 * Allows Tammy to manage:
 * - Business hours
 * - Contact information
 * 
 * Note: Announcements & promos are now managed in the Promos page.
 * 
 * Data persists to Supabase via the backend API.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { HiClock, HiSpeakerphone, HiPhone, HiCheck, HiExclamation, HiRefresh, HiArrowRight, HiCalendar, HiTrash } from 'react-icons/hi';
import { adminFetch, adminHeaders } from '../lib/adminApi';

const API_URL = import.meta.env.VITE_API_URL || '';

interface Closure {
  closure_date: string;
  label: string | null;
  created_at?: string;
}

interface BusinessHours {
  day: string;
  open: string;
  close: string;
  isClosed: boolean;
}

interface StoreSettings {
  phone: string;
  email: string;
  address: string;
  business_hours: BusinessHours[];
}

const DEFAULT_SETTINGS: StoreSettings = {
  phone: '(705) 503-4966',
  email: 'nannybbakedgoods@outlook.com',
  address: '62 Commerce Park Drive, Unit L, Barrie, ON L4N 8W8',
  business_hours: [
    { day: 'Monday', open: '', close: '', isClosed: true },
    { day: 'Tuesday', open: '11:00', close: '16:30', isClosed: false },
    { day: 'Wednesday', open: '11:00', close: '16:30', isClosed: false },
    { day: 'Thursday', open: '11:00', close: '16:30', isClosed: false },
    { day: 'Friday', open: '11:00', close: '16:30', isClosed: false },
    { day: 'Saturday', open: '10:00', close: '15:00', isClosed: false },
    { day: 'Sunday', open: '', close: '', isClosed: true },
  ],
};

export default function Settings() {
  const [settings, setSettings] = useState<StoreSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [closures, setClosures] = useState<Closure[]>([]);
  const [newClosureDate, setNewClosureDate] = useState('');
  const [newClosureLabel, setNewClosureLabel] = useState('');
  const [closureSaving, setClosureSaving] = useState(false);

  // Fetch settings from API
  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await adminFetch('/api/settings');
      if (!response.ok) throw new Error('Failed to fetch settings');
      
      const data = await response.json();
      setSettings({
        phone: data.phone || DEFAULT_SETTINGS.phone,
        email: data.email || DEFAULT_SETTINGS.email,
        address: data.address || DEFAULT_SETTINGS.address,
        business_hours: data.business_hours || DEFAULT_SETTINGS.business_hours,
      });
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError('Failed to load settings. Using defaults.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClosures = useCallback(async () => {
    try {
      const res = await adminFetch(`${API_URL}/api/settings/closures`);
      if (res.ok) {
        const data = await res.json();
        setClosures(data.closures || []);
      }
    } catch (err) {
      console.error('Error fetching closures:', err);
    }
  }, []);

  const addClosure = async () => {
    if (!newClosureDate.trim()) return;
    setClosureSaving(true);
    try {
      const res = await adminFetch(`${API_URL}/api/settings/closures`, {
        method: 'POST',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ closure_date: newClosureDate.trim(), label: newClosureLabel.trim() || null }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Failed to add');
      }
      setNewClosureDate('');
      setNewClosureLabel('');
      fetchClosures();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to add closure date');
    } finally {
      setClosureSaving(false);
    }
  };

  const removeClosure = async (date: string) => {
    if (!confirm(`Remove closure for ${date}?`)) return;
    try {
      const res = await adminFetch(`${API_URL}/api/settings/closures/${date}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to remove');
      fetchClosures();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to remove');
    }
  };

  // Load settings and closures on mount
  useEffect(() => {
    fetchSettings();
    fetchClosures();
  }, [fetchSettings, fetchClosures]);

  const handleHoursChange = (index: number, field: keyof BusinessHours, value: string | boolean) => {
    const newHours = [...settings.business_hours];
    const updated = { ...newHours[index], [field]: value };
    // When marking a day closed, clear stale open/close times so DB stays consistent.
    if (field === 'isClosed' && value === true) {
      updated.open = '';
      updated.close = '';
    }
    newHours[index] = updated;
    setSettings({ ...settings, business_hours: newHours });
  };

  const handleSettingsChange = (field: keyof StoreSettings, value: string) => {
    setSettings({ ...settings, [field]: value });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    
    try {
      const response = await adminFetch('/api/settings', {
        method: 'PUT',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(settings),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save settings');
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-500 mt-1">Manage your store settings</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchSettings}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh settings"
          >
            <HiRefresh className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              saved
                ? 'bg-green-600 text-white'
                : 'bg-purple-600 text-white hover:bg-purple-700'
            } disabled:opacity-50`}
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : saved ? (
              <>
                <HiCheck className="w-5 h-5" />
                Saved!
              </>
            ) : (
              <>
                <HiCheck className="w-5 h-5" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <HiExclamation className="w-5 h-5 text-red-600 flex-shrink-0" />
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Announcements & Promos Redirect */}
      <Link
        to="/admin/promos"
        className="block bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-xl p-6 hover:border-amber-300 transition-colors group"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-amber-100 rounded-lg">
              <HiSpeakerphone className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Discounts & Promotions</h2>
              <p className="text-sm text-gray-600">
                Create banners, popups, daily specials, and Square discount codes
              </p>
            </div>
          </div>
          <HiArrowRight className="w-6 h-6 text-amber-500 group-hover:translate-x-1 transition-transform" />
        </div>
      </Link>

      {/* Business Hours */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <HiClock className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Business Hours</h2>
            <p className="text-sm text-gray-500">Set your weekly schedule</p>
          </div>
        </div>

        <div className="space-y-3">
          {settings.business_hours.map((day, index) => (
            <div
              key={day.day}
              className={`flex items-center gap-4 p-3 rounded-lg ${
                day.isClosed ? 'bg-gray-50' : 'bg-white border border-gray-100'
              }`}
            >
              <div className="w-28 font-medium text-gray-700">{day.day}</div>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={day.isClosed}
                  onChange={(e) => handleHoursChange(index, 'isClosed', e.target.checked)}
                  className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                />
                <span className="text-sm text-gray-600">Closed</span>
              </label>

              {!day.isClosed && (
                <div className="flex items-center gap-2">
                  <input
                    type="time"
                    value={day.open}
                    onChange={(e) => handleHoursChange(index, 'open', e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  />
                  <span className="text-gray-400">to</span>
                  <input
                    type="time"
                    value={day.close}
                    onChange={(e) => handleHoursChange(index, 'close', e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  />
                </div>
              )}

              {day.isClosed && (
                <span className="text-sm text-gray-400 italic">Store closed</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Holiday / one-off closures */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-amber-100 rounded-lg">
            <HiCalendar className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Holiday / one-off closures</h2>
            <p className="text-sm text-gray-500">Add specific dates when the store is closed (e.g. Christmas). Orders for these dates cannot have a pickup time.</p>
          </div>
        </div>
        <ul className="space-y-2 mb-4">
          {closures.map((c) => (
            <li key={c.closure_date} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
              <span className="font-medium text-gray-900">{c.closure_date}</span>
              {c.label && <span className="text-sm text-gray-500">{c.label}</span>}
              <button
                type="button"
                onClick={() => removeClosure(c.closure_date)}
                className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                title="Remove"
              >
                <HiTrash className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Date</label>
            <input
              type="date"
              value={newClosureDate}
              onChange={(e) => setNewClosureDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Label (optional)</label>
            <input
              type="text"
              value={newClosureLabel}
              onChange={(e) => setNewClosureLabel(e.target.value)}
              placeholder="e.g. Christmas"
              className="px-3 py-2 border border-gray-300 rounded-lg w-40"
            />
          </div>
          <button
            type="button"
            onClick={addClosure}
            disabled={!newClosureDate.trim() || closureSaving}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50"
          >
            {closureSaving ? 'Adding...' : 'Add closure'}
          </button>
        </div>
      </div>

      {/* Contact Information */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-green-100 rounded-lg">
            <HiPhone className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Contact Information</h2>
            <p className="text-sm text-gray-500">Your business contact details</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              value={settings.phone}
              onChange={(e) => handleSettingsChange('phone', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email Address
            </label>
            <input
              type="email"
              value={settings.email}
              onChange={(e) => handleSettingsChange('email', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <input
              type="text"
              value={settings.address}
              onChange={(e) => handleSettingsChange('address', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Info Note */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <p className="text-sm text-purple-800">
          <strong>💡 Tip:</strong> Changes are saved to the database and will persist. 
          Business hours and contact info will update across your website automatically.
        </p>
      </div>
    </div>
  );
}
